import React, { useState, useRef, useEffect } from "react";
import { useUser } from "../../../../context/UserContext";
import * as FeatherIcon from "react-feather";
import UniversalShadowWrapper from "../../genericComponents/uiElements/UniversalShadowWrapper";
import { aiChatInit, getChatHistoryPerSession, saveChatHistory } from "../../../../services/iqApiV2";
import { useParams } from "react-router-dom";
import ChatSkeletonLoader from "./ChatSkeletonLoader";
import { getCurrentTime } from "../../../Helper/currDataTime";

export function extractFirstTitle(input = "") {
  // If not a string → return empty
  if (typeof input !== "string") return "";

  const trimmed = input.trim();

  // QUICK CHECK: If no "<" or ">" → it's plain text (not HTML)
  const looksLikeHTML = trimmed.includes("<") && trimmed.includes(">");

  if (!looksLikeHTML) {
    // It's a plain string → return as title
    return trimmed;
  }

  // Try extracting <title>...</title>
  const match = trimmed.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  if (match) {
    return match[1].trim();
  }

  // If no <title> tag exists → return fallback: plain text without tags
  const fallback = trimmed.replace(/<[^>]+>/g, "").trim();
  return fallback;
}


export function splitByMarker(raw) {
  // 1️⃣ Guard: If raw is null/undefined → return empty
  if (raw === null || raw === undefined) {
    return { before: "", after: "" };
  }

  // 2️⃣ Ensure raw is a STRING
  let text = "";
  if (typeof raw === "string") {
    text = raw;
  } else if (typeof raw === "object") {
    // Might be delta JSON → stringify safely
    try {
      text = JSON.stringify(raw);
    } catch {
      text = String(raw);
    }
  } else {
    text = String(raw);
  }

  const markers = [
    "*~EOF_TOC~**~ppt~*",
    "*~EOF_TOC~**~~*",
    "*~EOF_TOC~**~pdf~*",
    "*~EOF_TOC~**~html~*"
  ];

  for (const m of markers) {
    const pos = text.indexOf(m);
    if (pos !== -1) {
      return {
        before: text.substring(0, pos),   // ⬅️ OMIT the marker itself
        after: text.substring(pos + m.length).trim(),
      };
    }
  }

  // No marker found → return original in before
  return { before: text, after: "" };
}


export function extractContentAfterMarker(text) {
  if (!text || typeof text !== "string") return "";

  // Supported markers
  const markers = [
    "*~EOF_TOC~**~ppt~*",
    "*~EOF_TOC~**~~*",
    "*~EOF_TOC~**~pdf~*",
    "*~EOF_TOC~**~html~*"
  ];

  // Find which marker exists in the text
  for (const marker of markers) {
    const index = text?.indexOf(marker);
    if (index !== -1) {
      // Return everything AFTER the marker
      return text?.substring(index + marker.length).trim();
    }
  }

  // If no marker found → return original
  return text?.trim();
}


export function cleanHtmlMarkdownBlock(htmlString) {
  if (typeof htmlString !== 'string') return '';
  return htmlString
    .trim()
    .replace(/^```(?:html|HTML)?\s*/i, '')
    .replace(/```$/, '')
    .trim();
}

const parseApiDataToChat = (data) => {
  return data.flatMap(item => [
    {
      type: "user",
      content: item.question,
      time: getCurrentTime(item.modified_date?.$date),
    },
    {
      type: "bot",
      content: item.answer.trim(),
      time: getCurrentTime(item.modified_date?.$date),
      streaming: false,
    },
  ]);
};

function ChatbotCompStream({ chatSessionId, chat, setChat, sessionId, setSessionId,chatAccAndClient={accountName:"",clientName:""} }) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatHistloading, setChatHistLoading] = useState(false);
  const bottomRef = useRef(null);
  const chatBoxRef = useRef(null);
  const lastUserMessageRef = useRef(null); // NEW: Ref to last user message
  const { user } = useUser();
  const { accountName, clientName } = useParams();
  const [disableUserInput, setDisableUserInput] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const uniqueKey = `${Date.now()}_${user?.id}`;

  useEffect(() => {
    
    const fetchChatHistory = async () => {
      if (chatSessionId?.length > 0) {
        try {
          setChatHistLoading(true);
          const resp = await getChatHistoryPerSession(chatSessionId);
          if (resp?.length>0) {
              setChat(parseApiDataToChat(resp));
          setSessionId(chatSessionId);
          }
        
        } catch (err) {
          console.error("Failed to fetch chat history:", err);
        }
        setChatHistLoading(false);
      } else {
        setSessionId(uniqueKey);
      }
    };

    fetchChatHistory();
  }, [chatSessionId]);

  useEffect(() => {
    const el = chatBoxRef.current;
    if (!el) return;

    const handleScroll = () => {
      const hasScroll = el.scrollHeight > el.clientHeight;
      if (!hasScroll) {
        setIsAtBottom(true);
        return;
      }
      const threshold = 60;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      setIsAtBottom(atBottom);
    };

    el.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, [sessionId, chat]);

  useEffect(() => {
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }, 0);
  }, [sessionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = {
      type: "user",
      content: input,
      time: getCurrentTime(),
    };
    const botInitMessage = {
      type: "bot",
      content: "",
      time: getCurrentTime(),
      streaming: true,
    };

    setChat((prev) => [...prev, userMessage, botInitMessage]);
    setInput("");
    setLoading(true);
    setDisableUserInput(true);

    // Scroll the last user message to the top with some padding
    setTimeout(() => {
      const el = chatBoxRef.current;
      if (lastUserMessageRef.current && el) {
        const offsetTop = lastUserMessageRef.current.offsetTop;
        el.scrollTo({
          top: offsetTop - 20, // leave padding below
          behavior: "smooth",
        });
      }
    }, 100);

    try {
      const inputWithContext =
        chat?.length > 1
          ? `${input}\n\nHistory:\n\nUSER PROMPT: ${chat[chat.length - 2]?.content || ""}\n\nSYSTEM PROMPT: ${chat[chat.length - 1]?.content || ""}`
          : input;

      const currentClientName =  chatAccAndClient?.clientName != null ?  chatAccAndClient?.clientName: clientName;
      const currentAccountname = chatAccAndClient?.accountName != null? chatAccAndClient?.accountName : accountName;
      
      const res = await aiChatInit(inputWithContext, currentClientName,  currentAccountname);
      if (!res.body) throw new Error("No response body");

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let fullResponse = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");

        for (let chunk of chunks) {
          const dataLines = chunk
            .split("\n")
            .filter((line) => line.startsWith("data:"));
          const data = dataLines
            .map((line) => line.replace("data: ", ""))
            .join("\n");

          if (!data) continue;

          if (data === "[DONE]") {
            setChat((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                content: fullResponse,
                streaming: false,
                time: getCurrentTime(),
              };
              return updated;
            });

            setDisableUserInput(false);
            setLoading(false);

            try {
              const currentClientName =  chatAccAndClient?.clientName != null ?  chatAccAndClient?.clientName: clientName;
              const currentAccountname = chatAccAndClient?.accountName != null? chatAccAndClient?.accountName : accountName;
              
              await saveChatHistory(input, fullResponse, sessionId,currentClientName,currentAccountname);
            } catch (e) {
              console.error("Failed to save chat history:", e);
            }
            return;
          }

          fullResponse += data;
          setChat((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: fullResponse,
              streaming: true,
            };
            return updated;
          });
        }

        buffer = chunks[chunks.length - 1];
      }
    } catch (err) {
      console.error("Streaming failed:", err);
      setLoading(false);
      setDisableUserInput(false);
    }
  };

  useEffect(() => {
    
    if (user?.firstname) {
      setChat([
        {
          type: "bot",
          content: ` <p> Hi <strong>${user.firstname}</strong>, how can I help you today?</p>`,
          time: getCurrentTime(),
          streaming: false,
        },
      ]);
    }
  }, [user,chatSessionId]);

  return (
    <div style={styles.wrapper}>
      {chatHistloading ? (
        <ChatSkeletonLoader />
      ) : (
        <div style={styles.chatBox} ref={chatBoxRef}>
          {chat.map((entry, index) => {
            const isUser = entry.type === "user";
            return (
              <div
                key={index}
                ref={isUser ? lastUserMessageRef : null}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isUser ? "flex-end" : "flex-start",
                }}
              >
                <div style={styles.bubbleHeader}>
                  <span style={styles.nameLabel}>
                    {isUser ? user?.firstname : "SalesPlay"}
                  </span>
                </div>

                <div
                  style={{
                    ...styles.chatBubble,
                    backgroundColor: isUser ? "#4b70e2" : "#fff",
                    color: isUser ? "#fff" : "#3D4B82",
                  }}
                >
                  <div style={{ fontSize: "12px", fontWeight: "500", maxWidth: "100%" }}>
                    {isUser ? (
                      entry.content
                    ) : (
                      <>
                        <UniversalShadowWrapper htmlContent={cleanHtmlMarkdownBlock(entry.content)} />
                        {entry.streaming && <span className="blinking-cursor">|</span>}
                      </>
                    )}
                  </div>
                </div>
                <div style={styles.bubbleHeader}>
                  <span style={styles.timeLabel}>{entry.time}</span>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      )}

      {!isAtBottom && (
        <div
          onClick={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
          style={styles.scrollToBottomButton}
        >
          ↓ 
        </div>
      )}

      <div style={styles.stickyFooter}>
        <form onSubmit={handleSubmit} style={styles.form}>
          <textarea
            rows={10}
            style={styles.textarea}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder="Ask me anything..."
            disabled={disableUserInput}
          />
          <button
            type="submit"
            disabled={loading || disableUserInput}
            style={{
              ...styles.buttonsend,
              ...(loading || disableUserInput ? styles.buttonsendDisabled : {}),
            }}
          >
            <FeatherIcon.ArrowUp className="send_btn_custom_chat" />
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    width: "100%",
    margin: "1rem",
    display: "flex",
    flexDirection: "column",
    borderRadius: "0.1rem",
    overflow: "hidden",
    height: "100%",
    position: "relative",
  },
  chatBox: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    overflowY: "auto",
    padding: "0.625rem 0",
    maxHeight: "90%",
  },
  chatBubble: {
    maxWidth: "80%",
    padding: "0.6875rem 0.625rem",
    borderRadius: "1rem 1rem 1rem 0.5rem",
    fontSize: "0.75rem",
    fontWeight: "500",
    border: "0.0625rem solid #5B69A429",
    wordBreak: "break-word",
    animation: "fadeIn 0.3s ease-in",
  },
  bubbleHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.25rem",
    fontSize: "0.75rem",
    alignItems: "center",
    color: "#555",
    fontWeight: "bold",
    gap: "1rem",
  },
  nameLabel: {
    textTransform: "capitalize",
    color: "#4E6BCA",
    fontSize: "0.75rem",
    fontWeight: "500",
  },
  timeLabel: {
    fontWeight: "normal",
    color: "#999",
    fontSize: "0.7rem",
    marginTop: "0.3rem",
  },
  stickyFooter: {
    position: "sticky",
    bottom: 0,
    background: "#fff",
    padding: "1rem",
    borderTop: "0.0625rem solid #fff",
  },
  form: {
    display: "flex",
    gap: "0.5rem",
    position: "relative",
    alignItems: "center",
  },
  textarea: {
    flex: 1,
    resize: "none",
    padding: "0.75rem",
    fontSize: "0.75rem",
    borderRadius: "0.5rem",
    backgroundColor: "#eeeeee",
    maxHeight: "3.2rem",
    border: "0.0625rem solid #eee",
  },
  buttonsend: {
    padding: "0.5rem",
    backgroundColor: "#4b70e2",
    color: "#fff",
    width: "2rem",
    height: "2rem",
    fontWeight: "500",
    border: "none",
    borderRadius: "99rem",
    cursor: "pointer",
  },
  buttonsendDisabled: {
    backgroundColor: "#A9A9A9",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  scrollToBottomButton: {
    position: "absolute",
    bottom: "4.5rem",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(7, 16, 48, 0.53)",
    color: "#fff",
    borderRadius: "999px",
    padding: "0.4rem 0.75rem",
    fontSize: "0.75rem",
    cursor: "pointer",
    boxShadow: "0 0.125rem 0.375rem rgba(0,0,0,0.2)",
    zIndex: 10,
  },
};

export default ChatbotCompStream;
